/* eslint-disable */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import {
  ArrowRight,
  ShieldCheck,
  Clock,
  PhoneCall,
  Car,
  Calendar,
  MapPin,
  Star,
  CheckCircle2,
  Sparkles,
  Quote,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_IMG = "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?auto=format&fit=crop&w=1600&q=80";
const CITY_IMG = "https://images.unsplash.com/photo-1569701813229-33284b643e3c?auto=format&fit=crop&w=1200&q=80";

function money(n) {
  return Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function PublicNav() {
  return (
    <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-gray-200">
      <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="public-nav-home"><Logo /></Link>
        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-gray-700">
          <Link to="/fleet" className="hover:text-black" data-testid="public-nav-fleet">Fleet</Link>
          <a href="#how" className="hover:text-black">How it works</a>
          <a href="#about" className="hover:text-black">About</a>
          <a href="#contact" className="hover:text-black">Contact</a>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" className="hidden sm:inline-block text-sm text-gray-600 hover:text-black" data-testid="public-nav-staff">Staff login</Link>
          <Link to="/fleet">
            <Button className="bg-black hover:bg-gray-800 text-white" data-testid="public-nav-book">
              Book now <ArrowRight size={14} className="ml-1.5" />
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-gray-950 text-gray-300" id="contact">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-12 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div>
          <Logo variant="light" />
          <p className="text-sm text-gray-400 mt-4 max-w-xs">
            Philadelphia's premier weekly rental for rideshare drivers and everyday commuters.
          </p>
        </div>
        <div className="text-sm space-y-1.5">
          <div className="overline mb-2 text-gray-500">Contact</div>
          <div className="flex items-center gap-2"><PhoneCall size={14} className="text-amber-500" /> (215) 770-0679</div>
          <div className="flex items-center gap-2"><MapPin size={14} className="text-amber-500" /> Philadelphia, PA</div>
          <div className="flex items-center gap-2"><Clock size={14} className="text-amber-500" /> 7 days · 9am – 9pm</div>
        </div>
        <div className="text-sm space-y-1.5">
          <div className="overline mb-2 text-gray-500">Links</div>
          <Link to="/fleet" className="block hover:text-white">Available cars</Link>
          <a href="#how" className="block hover:text-white">How it works</a>
          <Link to="/login" className="block hover:text-white">Staff login</Link>
          <Link to="/privacy" className="block hover:text-white">Privacy Policy</Link>
          <Link to="/terms" className="block hover:text-white">Terms of Service</Link>
        </div>
      </div>
      <div className="border-t border-gray-800 py-4 text-center text-xs text-gray-500">
        © {new Date().getFullYear()} Fair Rideshare LLC. All rights reserved.
      </div>
    </footer>
  );
}

export default function Landing() {
  const [fleet, setFleet] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/fleet`).then((r) => setFleet(r.data)).catch(() => setFleet([]));
  }, []);

  const featured = fleet.slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/30" />
        </div>
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-20 md:py-32 text-white">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-full px-3 py-1 text-xs font-medium mb-6">
            <Sparkles size={12} className="text-amber-400" />
            Philly's #1 rideshare rental · Trusted by 50+ drivers
          </div>
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight max-w-3xl leading-[1.05]">
            Drive more.<br />
            <span className="text-amber-400">Earn more.</span><br />
            Skip the dealer.
          </h1>
          <p className="text-base md:text-lg text-gray-200 mt-6 max-w-xl">
            Weekly car rentals built for Philadelphia rideshare drivers and everyday commuters.
            Insurance-ready, mileage-friendly, no long contracts.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/fleet">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="hero-cta">
                Browse available cars <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <a href="#how">
              <Button size="lg" variant="outline" className="bg-white/0 border-white/30 text-white hover:bg-white/10">
                How it works
              </Button>
            </a>
          </div>
          <div className="mt-12 grid grid-cols-3 gap-4 max-w-md">
            <div>
              <div className="font-heading text-2xl md:text-3xl font-bold tabular-nums">{money(275)}</div>
              <div className="text-xs text-gray-300 uppercase tracking-wider mt-0.5">per week</div>
            </div>
            <div>
              <div className="font-heading text-2xl md:text-3xl font-bold tabular-nums">{money(200)}</div>
              <div className="text-xs text-gray-300 uppercase tracking-wider mt-0.5">deposit</div>
            </div>
            <div>
              <div className="font-heading text-2xl md:text-3xl font-bold tabular-nums">24h</div>
              <div className="text-xs text-gray-300 uppercase tracking-wider mt-0.5">approval</div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center gap-2.5"><ShieldCheck className="text-amber-600" size={18} /> Insurance-ready</div>
          <div className="flex items-center gap-2.5"><Clock className="text-amber-600" size={18} /> Same-day pickup</div>
          <div className="flex items-center gap-2.5"><Car className="text-amber-600" size={18} /> Rideshare approved</div>
          <div className="flex items-center gap-2.5"><PhoneCall className="text-amber-600" size={18} /> Live human support</div>
        </div>
      </section>

      {/* FEATURED FLEET */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 py-16 md:py-24" id="fleet">
        <div className="flex items-end justify-between mb-8 md:mb-12">
          <div>
            <div className="overline mb-1">The Fleet</div>
            <h2 className="font-heading text-3xl md:text-5xl font-extrabold tracking-tight">Pick your ride</h2>
          </div>
          <Link to="/fleet" className="text-sm font-medium text-gray-700 hover:text-black hidden md:inline-flex items-center gap-1.5" data-testid="see-all-fleet">
            See all <ArrowRight size={14} />
          </Link>
        </div>
        {featured.length === 0 ? (
          <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-12 text-center">
            <Car className="mx-auto text-gray-400 mb-3" size={32} />
            <p className="text-gray-600">Vehicles being added — call us to reserve one today.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map((v) => (
              <VehicleCard key={v.id} v={v} onClick={() => nav(`/book/${v.id}`)} />
            ))}
          </div>
        )}
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-gray-950 text-white" id="how">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="overline mb-2 text-amber-400">How it works</div>
          <h2 className="font-heading text-3xl md:text-5xl font-extrabold tracking-tight mb-12 max-w-2xl">
            On the road in three steps.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Browse the fleet", d: "Pick a car that fits your schedule and your hustle. Weekly rates, deposit returnable on time return.", icon: Car },
              { n: "02", t: "Book online", d: "Choose your pickup window and submit your info. Confirmation by SMS in minutes — no phone tag.", icon: Calendar },
              { n: "03", t: "Sign & drive", d: "Sign the agreement on your phone, bring your license and insurance, drive off. We handle the rest.", icon: CheckCircle2 },
            ].map((s) => (
              <div key={s.n} className="border border-white/10 rounded-xl p-6 bg-white/[0.03]">
                <div className="flex items-center justify-between mb-4">
                  <div className="font-mono text-amber-400 text-xs">{s.n}</div>
                  <s.icon size={20} className="text-amber-400" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-2">{s.t}</h3>
                <p className="text-sm text-gray-300 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline mb-2">Drivers love it</div>
            <h2 className="font-heading text-3xl md:text-5xl font-extrabold tracking-tight mb-6">
              "Best decision I made for my Uber side hustle."
            </h2>
            <Quote className="text-amber-500 mb-4" size={28} />
            <p className="text-gray-700 leading-relaxed text-lg">
              No dealership runaround, no surprise fees. I signed the contract on my phone Sunday night and was driving by Monday morning. The team responds fast and the car was spotless.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-amber-100 grid place-items-center font-heading font-bold text-amber-700">CW</div>
              <div>
                <div className="font-semibold">Calvin W.</div>
                <div className="text-xs text-gray-500">Rideshare driver · Philadelphia</div>
              </div>
              <div className="ml-auto flex gap-0.5 text-amber-500">
                {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={14} fill="currentColor" />)}
              </div>
            </div>
          </div>
          <div className="relative">
            <img src={CITY_IMG} alt="" className="rounded-xl shadow-xl object-cover w-full h-[400px]" />
            <div className="absolute -bottom-5 -left-5 bg-white border border-gray-200 rounded-xl shadow-lg p-4 max-w-[220px] hidden md:block">
              <div className="flex gap-0.5 text-amber-500 mb-1">
                {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={12} fill="currentColor" />)}
              </div>
              <div className="text-xs text-gray-600 leading-snug">"Insurance was already set up — picked the car up in 20 minutes."</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="bg-amber-500">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-12 md:py-16 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-black max-w-xl">
              Ready to roll? Book in 2 minutes.
            </h2>
            <p className="text-sm text-black/70 mt-2">No long contracts. Cancel anytime. Insurance-ready cars.</p>
          </div>
          <Link to="/fleet">
            <Button size="lg" className="bg-black hover:bg-gray-800 text-white font-semibold shrink-0" data-testid="cta-banner">
              Reserve your car <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export function VehicleCard({ v, onClick }) {
  const fallback = `https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=900&q=80`;
  const img = v.photo || fallback;
  const available = v.status === "Available";
  return (
    <button
      onClick={onClick}
      className="text-left bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-gray-900 hover:shadow-lg transition group"
      data-testid={`vehicle-card-${v.id}`}
    >
      <div className="aspect-[16/10] bg-gray-100 overflow-hidden">
        <img src={img} alt={`${v.year} ${v.make} ${v.model}`} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-heading font-bold text-lg leading-tight">{v.year} {v.make} {v.model}</div>
            <div className="text-xs text-gray-500 mt-0.5 font-mono">{v.plate || "—"}</div>
          </div>
          <div className={`pill ${available ? "pill-green" : "pill-yellow"}`}>{available ? "Available" : v.status}</div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 text-center">
          <div>
            <div className="font-heading text-xl font-bold tabular-nums">{money(v.weekly_rate)}</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">per week</div>
          </div>
          <div>
            <div className="font-heading text-xl font-bold tabular-nums">{money(v.deposit_amount)}</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">deposit</div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100 text-sm font-medium text-amber-700 inline-flex items-center gap-1.5">
          Reserve this car <ArrowRight size={14} />
        </div>
      </div>
    </button>
  );
}
